import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import MEMBERSHIP_OFFERED_ITEMS from '../../utils/membership';
import { isMobile, SIZE_XS, SIZE_SM } from '../../../../hooks/useBreakPoint';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import { getConfigurations } from '../../utils/service';
import { SUBSCRIPTION_STEPS } from '../../utils/subscriptions';

export const MEMBERSHIP_ORDER_TBB_STEPS = {
  PRODUCT_SELECTION: 'PRODUCT_SELECTION',
  ADDRESSES_CONTAINER: 'ADDRESSES_CONTAINER',
  ORDER_CONFIRMATION: 'ORDER_CONFIRMATION',
};

export const MEMBERSHIP_ORDER_TBB_TYPE = {
  MEMBERSHIP_CLAIM: 'MEMBERSHIP_CLAIM',
  MEMBERSHIP_FIRST: 'MEMBERSHIP_FIRST',
  ONE_TIME: 'ONE_TIME',
};

const MembershipOrderTbbContext = createContext();

export const useMembershipOrderTbbContext = () => {
  const context = useContext(MembershipOrderTbbContext);
  if (!context) {
    throw new Error('useMembershipOrderTbbContext must be used within a MembershipOrderTbbProvider');
  }
  return context;
};

const initialState = {
  currentStep: MEMBERSHIP_ORDER_TBB_STEPS.PRODUCT_SELECTION,
  selectedItems: Object.keys(MEMBERSHIP_OFFERED_ITEMS)
    .reduce((acc, key) => ({ ...acc, [key]: 0 }), {}),
  selectedAddress: null,
  membershipAddresses: [],
  refetchMembershipAddresses: false,
  orderType: MEMBERSHIP_ORDER_TBB_TYPE.MEMBERSHIP_CLAIM,
};

export const MembershipOrderTbbProvider = ({
  children,
  defaultValues,
  defaultStep,
  defaultSelectedAddress,
  onProductsChange,
  onAddressChange,
  onStepChange,
  orderType,
  isAnonymous,
  defaultMessages = {},
}) => {
  const isMobileSize = isMobile([SIZE_XS, SIZE_SM]);
  const { isSignedIn } = useFirebaseAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(defaultStep || initialState.currentStep);
  const [selectedItems, setSelectedItems] = useState(
    defaultValues || initialState.selectedItems,
  );
  const [selectedAddress, setSelectedAddress] = useState(
    defaultSelectedAddress || initialState.selectedAddress,
  );
  const [membershipAddresses, setMembershipAddresses] = useState(initialState.membershipAddresses);
  const [refetchMembershipAddresses, setRefetchMembershipAddresses] =
    useState(initialState.refetchMembershipAddresses);
  const [messages, setMessages] = useState({});

  const navigateToStep = (step) => {
    setCurrentStep(step);
  };

  const goBackToProductSelection = () => {
    setCurrentStep(MEMBERSHIP_ORDER_TBB_STEPS.PRODUCT_SELECTION);
  };

  const resetOrder = () => {
    setRefetchMembershipAddresses(true);
    setCurrentStep(initialState.currentStep);
    setSelectedItems(initialState.selectedItems);
  };

  const {
    isFirstOrder,
    isOneTimeOrder,
  } = useMemo(() => ({
    isFirstOrder: orderType === MEMBERSHIP_ORDER_TBB_TYPE.MEMBERSHIP_FIRST,
    isOneTimeOrder: orderType === MEMBERSHIP_ORDER_TBB_TYPE.ONE_TIME,
  }), [orderType]);

  const getMessages = useCallback(async (messagesData) => {
    if (Object.keys(messagesData).length > 0) {
      setMessages(messagesData);
      return;
    }

    const {
      value: configValue,
      error: configError,
    } = await getConfigurations('membershipSubscriptionConfig');

    if (configError) {
      setMessages({});
      return;
    }

    setMessages(
      configValue?.steps?.[SUBSCRIPTION_STEPS.CONFIRMATION]?.messages || {},
    );
  }, []);

  useEffect(() => {
    if (onProductsChange && typeof onProductsChange === 'function') {
      onProductsChange(selectedItems);
    }
  }, [selectedItems, onProductsChange]);

  useEffect(() => {
    if (onAddressChange && typeof onAddressChange === 'function') {
      onAddressChange(selectedAddress);
    }
  }, [selectedAddress, onAddressChange]);

  useEffect(() => {
    if (onStepChange && typeof onStepChange === 'function') {
      onStepChange(currentStep);
    }
  }, [currentStep, onStepChange]);

  useEffect(() => {
    getMessages(defaultMessages);
  }, []);

  const value = useMemo(
    () => ({
      currentStep,
      selectedItems,
      selectedAddress,
      membershipAddresses,
      navigateToStep,
      goBackToProductSelection,
      setSelectedItems,
      setSelectedAddress,
      setMembershipAddresses,
      refetchMembershipAddresses,
      setRefetchMembershipAddresses,
      isLoading,
      setIsLoading,
      error,
      setError,
      resetOrder,
      isFirstOrder,
      isOneTimeOrder,
      isAnonymous,
      isSignedIn,
      withDefaultValues: Object.values(defaultValues || {}).some(dvalue => dvalue > 0),
      isMobileSize,
      messages,
    }),
    [currentStep,
      selectedItems,
      selectedAddress,
      membershipAddresses,
      refetchMembershipAddresses,
      setRefetchMembershipAddresses,
      isLoading,
      setIsLoading,
      error,
      setError,
      setSelectedItems,
      resetOrder,
      isFirstOrder,
      isOneTimeOrder,
      isAnonymous,
      isSignedIn,
      defaultValues,
      isMobileSize,
      messages,
    ],
  );

  return (
    <MembershipOrderTbbContext.Provider value={value}>
      {children}
    </MembershipOrderTbbContext.Provider>
  );
};

export default MembershipOrderTbbContext;
